import React, { useState, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChatContext } from '../../context/ChatContext';
import { useToast } from '../commons/toast/ToastContext';
import { FiX, FiMessageSquare, FiUsers, FiUserPlus } from 'react-icons/fi';
import axios from '../../axiosConfig';

const CHAT_TYPE_KEYS = ['DIRECT', 'CUSTOM_GROUP', 'TRAINER_CHAT', 'TRAINING_GROUP', 'SCHOOL_ANNOUNCEMENT'];
const CHAT_TYPE_ICONS = {
    DIRECT: '✉️',
    CUSTOM_GROUP: '💬',
    TRAINER_CHAT: '👥',
    TRAINING_GROUP: '🐕',
    SCHOOL_ANNOUNCEMENT: '📢'
};

const OWNER_CHAT_TYPE_KEYS = ['DIRECT'];

const CreateChatModal = ({ type: initialType, schoolId, userRole, userId, onClose }) => {
    const { t } = useTranslation('chat');
    const {
        createDirectChat,
        createCustomGroupChat,
        createTrainerChat,
        createTrainingGroupChat,
        createSchoolAnnouncementChat,
        openChatRoom
    } = useContext(ChatContext);
    const toast = useToast();

    const fetchAllOwners = async (schoolId) => {
        const allOwners = [];
        let page = 0;
        let lastPage = false;
        while (!lastPage) {
            const res = await axios.get(`/api/v1/owner/all/${schoolId}?page=${page}&size=100`);
            allOwners.push(...(res.data?.content || []));
            lastPage = res.data?.last !== false;
            page++;
        }
        return allOwners.map(o => ({
            id: o.id, name: `${o.firstName} ${o.lastName}`, role: t('participants_panel.roleOwner')
        }));
    };

    // If owner (only 1 type available) and no explicit type, skip type selection
    const ownerAutoType = userRole !== 'ROLE_TRAINER' ? 'DIRECT' : null;
    const [step, setStep] = useState(initialType || ownerAutoType ? 'form' : 'type');
    const [selectedType, setSelectedType] = useState(initialType || ownerAutoType);
    const [chatName, setChatName] = useState('');
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [availableUsers, setAvailableUsers] = useState([]);
    const [trainingGroups, setTrainingGroups] = useState([]);
    const [selectedGroupId, setSelectedGroupId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    const isTrainer = userRole === 'ROLE_TRAINER';
    const chatTypeKeys = isTrainer ? CHAT_TYPE_KEYS : OWNER_CHAT_TYPE_KEYS;

    useEffect(() => {
        if (step === 'form' && schoolId) {
            loadFormData();
        }
    }, [step, selectedType, schoolId]);

    const loadFormData = async () => {
        setLoading(true);
        try {
            if (selectedType === 'DIRECT') {
                if (isTrainer) {
                    const [trainersRes, owners] = await Promise.all([
                        axios.get(`/api/v1/trainer/find-all/${schoolId}`),
                        fetchAllOwners(schoolId)
                    ]);
                    const trainers = (trainersRes.data || []).map(tr => ({
                        id: tr.id, name: `${tr.firstName} ${tr.lastName}`, role: t('participants_panel.roleTrainer')
                    }));
                    setAvailableUsers([...trainers, ...owners]);
                } else {
                    // Owner can only chat with trainers from their school
                    const response = await axios.get(`/api/v1/trainer/find-all/${schoolId}`);
                    setAvailableUsers((response.data || []).map(tr => ({
                        id: tr.id, name: `${tr.firstName} ${tr.lastName}`, role: t('participants_panel.roleTrainer')
                    })));
                }
            } else if (selectedType === 'CUSTOM_GROUP') {
                const [trainersRes, owners] = await Promise.all([
                    axios.get(`/api/v1/trainer/find-all/${schoolId}`),
                    fetchAllOwners(schoolId)
                ]);
                const trainers = (trainersRes.data || []).map(tr => ({
                    id: tr.id, name: `${tr.firstName} ${tr.lastName}`, role: t('participants_panel.roleTrainer')
                }));
                setAvailableUsers([...trainers, ...owners]);
            } else if (selectedType === 'TRAINER_CHAT') {
                const response = await axios.get(`/api/v1/trainer/find-all/${schoolId}`);
                setAvailableUsers((response.data || []).map(tr => ({
                    id: tr.id, name: `${tr.firstName} ${tr.lastName}`, role: t('participants_panel.roleTrainer')
                })));
            } else if (selectedType === 'TRAINING_GROUP') {
                const response = await axios.get(`/api/v1/groups/school/${schoolId}`);
                setTrainingGroups(response.data || []);
            }
        } catch (err) {
            console.error('Failed to load data:', err);
            toast.error(t('create.loadDataError'));
        } finally {
            setLoading(false);
        }
    };

    const handleSelectType = (type) => {
        setSelectedType(type);
        setStep('form');
    };

    const toggleUser = (uId) => {
        if (selectedType === 'DIRECT') {
            setSelectedUsers([uId]);
        } else {
            setSelectedUsers(prev =>
                prev.includes(uId) ? prev.filter(id => id !== uId) : [...prev, uId]
            );
        }
    };

    const handleCreate = async () => {
        setLoading(true);
        try {
            let result;
            switch (selectedType) {
                case 'DIRECT':
                    if (selectedUsers.length !== 1) {
                        toast.error(t('create.validation.selectOneUser'));
                        return;
                    }
                    result = await createDirectChat(selectedUsers[0]);
                    break;
                case 'CUSTOM_GROUP':
                    if (!chatName.trim()) {
                        toast.error(t('create.validation.enterChatName'));
                        return;
                    }
                    if (selectedUsers.length === 0) {
                        toast.error(t('create.validation.selectAtLeastOneParticipant'));
                        return;
                    }
                    result = await createCustomGroupChat(chatName.trim(), selectedUsers);
                    break;
                case 'TRAINER_CHAT':
                    if (selectedUsers.length === 0) {
                        toast.error(t('create.validation.selectAtLeastOneTrainer'));
                        return;
                    }
                    result = await createTrainerChat(selectedUsers, chatName.trim() || null);
                    break;
                case 'TRAINING_GROUP':
                    if (!selectedGroupId) {
                        toast.error(t('create.validation.selectTrainingGroup'));
                        return;
                    }
                    result = await createTrainingGroupChat(selectedGroupId);
                    break;
                case 'SCHOOL_ANNOUNCEMENT':
                    result = await createSchoolAnnouncementChat(schoolId);
                    break;
                default:
                    return;
            }

            if (result?.id) {
                openChatRoom(result.id);
            }
            toast.success(t('create.created'));
            onClose();
        } catch (err) {
            toast.error(err.message || t('create.createError'));
        } finally {
            setLoading(false);
        }
    };

    const filteredUsers = availableUsers.filter(u =>
        u.id !== userId && u.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const getTypeDesc = (typeKey) => {
        if (typeKey === 'DIRECT' && !isTrainer) {
            return t(`create.types.${typeKey}.ownerDesc`);
        }
        return t(`create.types.${typeKey}.desc`);
    };

    return (
        <div className="chat-modal-overlay" onClick={onClose}>
            <div className="chat-modal" onClick={(e) => e.stopPropagation()}>
                <div className="chat-modal-header">
                    <h3>{step === 'type' ? t('create.title') : t('create.createChat')}</h3>
                    <button className="chat-modal-close" onClick={onClose}>
                        <FiX />
                    </button>
                </div>

                <div className="chat-modal-body">
                    {step === 'type' && (
                        <div className="chat-type-grid">
                            {chatTypeKeys.map(typeKey => (
                                <button
                                    key={typeKey}
                                    className="chat-type-card"
                                    onClick={() => handleSelectType(typeKey)}
                                >
                                    <span className="chat-type-icon">{CHAT_TYPE_ICONS[typeKey]}</span>
                                    <span className="chat-type-label">{t(`create.types.${typeKey}.label`)}</span>
                                    <span className="chat-type-desc">{getTypeDesc(typeKey)}</span>
                                </button>
                            ))}
                        </div>
                    )}

                    {step === 'form' && (
                        <div className="chat-create-form">
                            {selectedType === 'SCHOOL_ANNOUNCEMENT' ? (
                                <div className="chat-form-info">
                                    <p>{t('create.announcementInfo1')}</p>
                                    <p>{t('create.announcementInfo2')}</p>
                                    <p>{t('create.announcementInfo3')}</p>
                                </div>
                            ) : selectedType === 'TRAINING_GROUP' ? (
                                <div className="chat-form-section">
                                    <label>{t('create.selectTrainingGroup')}</label>
                                    {loading ? (
                                        <p>{t('loading')}</p>
                                    ) : trainingGroups.length === 0 ? (
                                        <p>{t('create.noTrainingGroups')}</p>
                                    ) : (
                                        <div className="chat-group-select">
                                            {trainingGroups.map(group => (
                                                <button
                                                    key={group.id}
                                                    className={`chat-group-option ${selectedGroupId === group.id ? 'selected' : ''}`}
                                                    onClick={() => setSelectedGroupId(group.id)}
                                                >
                                                    <span>{group.name}</span>
                                                    <span className="chat-group-count">
                                                        {t('create.dogsCount', { current: group.currentDogCount, max: group.maxCapacity })}
                                                    </span>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <>
                                    {(selectedType === 'CUSTOM_GROUP' || selectedType === 'TRAINER_CHAT') && (
                                        <div className="chat-form-section">
                                            <label>{t('create.chatName')}</label>
                                            <input
                                                type="text"
                                                value={chatName}
                                                onChange={(e) => setChatName(e.target.value)}
                                                placeholder={t('create.chatNamePlaceholder')}
                                                className="chat-form-input"
                                                maxLength={255}
                                            />
                                        </div>
                                    )}

                                    <div className="chat-form-section">
                                        <label>
                                            {selectedType === 'DIRECT'
                                                ? t('create.selectUser')
                                                : t('create.selectParticipants')}
                                        </label>
                                        <input
                                            type="text"
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            placeholder={t('create.searchPlaceholder')}
                                            className="chat-form-input"
                                        />
                                        {loading ? (
                                            <p>{t('loading')}</p>
                                        ) : (
                                            <div className="chat-user-select-list">
                                                {filteredUsers.map(user => (
                                                    <label key={user.id} className="chat-user-select-item">
                                                        <input
                                                            type={selectedType === 'DIRECT' ? 'radio' : 'checkbox'}
                                                            name="user-select"
                                                            checked={selectedUsers.includes(user.id)}
                                                            onChange={() => toggleUser(user.id)}
                                                        />
                                                        <span>{user.name}</span>
                                                        <span className="chat-user-role-tag">{user.role}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>

                {step === 'form' && (
                    <div className="chat-modal-footer">
                        <button
                            className="chat-btn chat-btn-secondary"
                            onClick={() => {
                                setStep('type');
                                setSelectedUsers([]);
                                setChatName('');
                                setSelectedGroupId(null);
                            }}
                        >
                            {t('create.back')}
                        </button>
                        <button
                            className="chat-btn chat-btn-primary"
                            onClick={handleCreate}
                            disabled={loading}
                        >
                            {loading ? t('create.creating') : t('create.createChat')}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CreateChatModal;
